import { Link } from "react-router-dom";

/**
 * Compact breadcrumb trail. Pass `items` as an array of { label, to } — the
 * last item (current page) should omit `to` and renders as plain text.
 *
 *   <Breadcrumbs items={[
 *     { label: "Home", to: "/" },
 *     { label: "Catalog", to: "/catalog" },
 *     { label: item.name },
 *   ]} />
 */
export default function Breadcrumbs({ items, className = "" }) {
  const trail = (items ?? []).filter(Boolean);
  if (!trail.length) return null;

  // "One level up" = the nearest ancestor that is a real link (skip the current
  // page, which is the last item and has no `to`).
  const parent = [...trail.slice(0, -1)].reverse().find((it) => it.to);

  return (
    <nav aria-label="Breadcrumb" className={`pmp-breadcrumb ${className}`.trim()}>
      {parent ? (
        <Link
          to={parent.to}
          className="pmp-breadcrumb-back"
          aria-label={`Back to ${parent.label}`}
        >
          <span aria-hidden="true">←</span>
        </Link>
      ) : null}
      <ol className="pmp-breadcrumb-list">
        {trail.map((item, i) => {
          const isLast = i === trail.length - 1;
          return (
            <li
              key={`${item.label}-${i}`}
              className="pmp-breadcrumb-item"
              aria-current={isLast ? "page" : undefined}
            >
              {item.to && !isLast ? (
                <Link to={item.to} className="pmp-breadcrumb-link">
                  {item.label}
                </Link>
              ) : (
                <span className="pmp-breadcrumb-current">{item.label}</span>
              )}
              {isLast ? null : (
                <span className="pmp-breadcrumb-sep" aria-hidden="true">
                  /
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
